"use client";

/** When a boundary hydrates on its own: near the viewport, or on a pointer, touch or focus inside. */
export type DeferredHydrationTrigger = "visible" | "interaction";

export interface Gate {
  trigger: DeferredHydrationTrigger;
  /** Resolved (with React's thenable fields set) once the group may hydrate. */
  promise: Promise<void> & { status?: "fulfilled"; value?: undefined };
  released: boolean;
  release: () => void;
  /** The wrappers rendering the group's boundaries, re-rendered on release (DeferredHydration). */
  listeners: Set<() => void>;
}

const gates = new Map<string, Gate>();

// Gates only hold content back while the page that the server rendered hydrates (src/client.tsx
// arms them). Server rendering, tests and client-side renders always render the content.
let armed = false;

/** Whether gates hold content back (`armDeferredHydration`). */
export const isDeferredHydrationArmed = () => armed;

export const getGate = (group: string, trigger: DeferredHydrationTrigger): Gate => {
  let gate = gates.get(group);
  if (!gate) {
    let resolve!: () => void;
    const promise: Gate["promise"] = new Promise<void>((done) => {
      resolve = done;
    });
    const created: Gate = {
      trigger,
      promise,
      released: false,
      listeners: new Set(),
      release: () => {
        if (created.released) return;
        created.released = true;
        promise.status = "fulfilled";
        promise.value = undefined;
        resolve();
        for (const listener of created.listeners) listener();
      },
    };
    gate = created;
    gates.set(group, gate);
  }
  return gate;
};

/** Hydrates every boundary of a group now (no-op for groups that hydrated or never rendered). */
export const hydrateDeferredGroup = (group: string) => {
  gates.get(group)?.release();
};

/** Hydrates every boundary waiting for the given trigger, e.g. all cards before they show new data. */
export const hydrateAllDeferred = (trigger: DeferredHydrationTrigger) => {
  for (const gate of gates.values()) if (gate.trigger === trigger) gate.release();
};

// The wrapper's attributes (see DeferredHydration.tsx), in the server HTML: they name its group
// and trigger, so an interaction can release the group before React has hydrated the wrapper.
const GROUP_ATTRIBUTE = "data-deferred-hydration";

const TRIGGER_ATTRIBUTE = "data-deferred-hydration-trigger";

// Pointer, touch and focus signal an interaction about to happen; pointerdown and click are the
// discrete events React hydrates waiting content for (a click from assistive technology or a
// script comes without a pointerover first).
const INTERACTION_EVENTS = [
  "pointerover",
  "pointerdown",
  "touchstart",
  "focusin",
  "click",
] as const;

// Releases the groups of every wrapper an interaction lands in. One capture listener on the window,
// added before hydration, sees each event before React does, so a click hydrates its content. It
// reads the wrappers from the DOM rather than from the wrappers' effects: while the page around a
// card is still hydrating, the wrapper hasn't run them, and a click React then hydrates the card
// for would find its gate shut, suspend, and be dropped (React doesn't replay discrete events).
// React's own listener stops continuous events (pointerover, focusin) aimed at content that hasn't
// hydrated, to replay them once it has, so listeners on the wrappers themselves would never hear
// them.
const onInteraction = (event: Event) => {
  const target = event.target;
  let wrapper = target instanceof Element ? target.closest(`[${GROUP_ATTRIBUTE}]`) : null;
  while (wrapper) {
    const group = wrapper.getAttribute(GROUP_ATTRIBUTE) as string;
    const trigger = wrapper.getAttribute(TRIGGER_ATTRIBUTE) as DeferredHydrationTrigger;
    getGate(group, trigger).release();
    wrapper = wrapper.parentElement?.closest(`[${GROUP_ATTRIBUTE}]`) ?? null;
  }
};

/** Lets deferred boundaries keep their server HTML while the document hydrates. Browser entry only. */
export const armDeferredHydration = (enabled = true) => {
  armed = enabled;
  for (const type of INTERACTION_EVENTS) {
    if (enabled) {
      window.addEventListener(type, onInteraction, { capture: true, passive: true });
    } else {
      window.removeEventListener(type, onInteraction, { capture: true });
    }
  }
};
